/**
 * @file RepSendModal.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-19
 * @modified 2026-10-28
 * @description Pricing & Costing: ask reps for prices, checked before anything
 *              goes (Cam, 19 Oct 2026: "It should open a list for her to check
 *              before sending, not just send them all out … a preview of the
 *              email and XLSX per rep email going out … so they can edit the
 *              body"). One email per rep, each carrying ONE spreadsheet of all
 *              that rep's requests (combined by customer, product and pricing
 *              window — services/priceRequestService).
 *
 *              Left: every rep the chosen lines go to, each ticked to send (a
 *              rep with no email address cannot be). Right: the chosen rep's
 *              email — To, Cc, Subject and Body all editable — and what their
 *              spreadsheet holds, as it will be attached. A line from Held rep
 *              prices goes as a second price check: the subject, body and
 *              spreadsheet say so and show the rep's earlier price (still
 *              editable). Send asks once, then sends only the ticked reps' emails
 *              and says which went.
 *
 *              1.1.0 (Cam, 19 Oct — "if there is a cost price change then
 *              split group by that for reps"): `costs` (order line id → the
 *              cost each line will be confirmed at, as the screen shows it)
 *              goes with the preview, the download and the send, so a request
 *              is split where its lines' costs differ and says each cost.
 *
 *              1.2.0 (Cam, 28 Oct 2026): `resendOf` — "Send again" from the
 *              record of an earlier send (RepRequestHistory): today's figures,
 *              recorded as asked again from that send. The spreadsheet's
 *              preview shows the previous sales' merged headings (each
 *              column's `group`) over their columns, as the workbook does.
 *
 *              /api/pricing-costing/rep-requests/preview | /spreadsheet | /send.
 */

import React, { useEffect, useMemo, useState } from 'react';
import Modal from './Modal';
import Input from './Input';
import Textarea from './Textarea';
import TickCheckbox from './TickCheckbox';
import LoadingSpinner from './LoadingSpinner';
import ConfirmDialog from './ConfirmDialog';
import SenderSelect from './SenderSelect';
import { useSlideOutButton } from './SlideOutButton';
import { API_URL } from '../config';
import './PricingCosting.css';

function RepSendModal({ lineIds, costs = {}, resendOf = null, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [groups, setGroups] = useState([]);
  const [unassigned, setUnassigned] = useState(0);
  const [current, setCurrent] = useState(0);
  const [sendAs, setSendAs] = useState('me');
  const [asking, setAsking] = useState(false);
  const [sending, setSending] = useState(false);
  const [sentAny, setSentAny] = useState(false);

  useEffect(() => {
    let gone = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/pricing-costing/rep-requests/preview`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ lineIds, costs })
        });
        const body = await res.json();
        if (!res.ok || !body.success) throw new Error(body.error || 'The emails could not be prepared');
        if (gone) return;
        const list = (body.groups || []).map(g => ({
          ...g,
          include: !!(g.repId && g.repEmail),
          to: g.repEmail || '', cc: '', subject: g.subject || '', body: g.body || '',
          status: null, message: null
        }));
        setGroups(list);
        setUnassigned(body.unassignedCount || 0);
        setCurrent(Math.max(0, list.findIndex(g => g.repId)));
      } catch (err) {
        if (!gone) setError(err.message);
      } finally {
        if (!gone) setLoading(false);
      }
    })();
    return () => { gone = true; };
  }, [lineIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const g = groups[current] || null;
  const sendable = (x) => x.repId && x.include && String(x.to || '').trim() && x.status !== 'sent';
  const toSend = useMemo(() => groups.filter(sendable), [groups]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (i, patch) => setGroups(prev => prev.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const allTicked = groups.filter(x => x.repId).every(x => x.include);

  const download = async () => {
    if (!g) return;
    try {
      const res = await fetch(`${API_URL}/api/pricing-costing/rep-requests/spreadsheet`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ lineIds: g.lineIds, costs })
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = g.sheet?.filename || 'Price request.xlsx';
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (err) {
      setError(err.message);
    }
  };

  const send = async () => {
    setSending(true);
    setError(null);
    const batch = toSend.map(x => ({ lineIds: x.lineIds, to: x.to, cc: x.cc, subject: x.subject, body: x.body, sendAs, ...(resendOf ? { resendOf } : {}) }));
    groups.forEach((x, i) => { if (sendable(x)) set(i, { status: 'sending' }); });
    try {
      const res = await fetch(`${API_URL}/api/pricing-costing/rep-requests/send`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ groups: batch, costs })
      });
      const body = await res.json();
      if (!res.ok || !body.success) throw new Error(body.error || 'Nothing was sent');
      const results = body.results || [];
      setGroups(prev => prev.map(x => {
        const r = results.find(y => y.to === x.to && (y.lineIds || []).join(',') === (x.lineIds || []).join(','));
        if (!r) return x.status === 'sending' ? { ...x, status: null } : x;
        return { ...x, status: r.outcome === 'sent' ? 'sent' : 'failed', message: r.message, include: r.outcome !== 'sent' && x.include };
      }));
      if (results.some(r => r.outcome === 'sent')) setSentAny(true);
    } catch (err) {
      setError(err.message);
      setGroups(prev => prev.map(x => (x.status === 'sending' ? { ...x, status: null } : x)));
    } finally {
      setSending(false);
    }
  };

  const close = () => onClose(sentAny);

  const footer = (
    <div className="buttons slideout">
      <div>
        <button type="button" className="btn-slideout" onClick={close}>
          <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
          <span className="btn-label">{sentAny ? 'Done' : 'Cancel'}</span>
        </button>
      </div>
      <div>
        <button type="button" className="btn-slideout" onClick={download} disabled={!g || !g.repId}
          title="Download this rep's spreadsheet as it will be attached">
          <div className="icon-wrapper"><span className="material-symbols-rounded">download</span></div>
          <span className="btn-label">Download XLSX</span>
        </button>
      </div>
      <div>
        <button type="button" className="btn-slideout" onClick={() => setAsking(true)} disabled={sending || !toSend.length}
          title={toSend.length ? '' : 'Tick at least one rep with an email address'}>
          <div className="icon-wrapper"><span className="material-symbols-rounded">send</span></div>
          <span className="btn-label">{sending ? 'Sending…' : `Send ${toSend.length} email${toSend.length === 1 ? '' : 's'}`}</span>
        </button>
      </div>
    </div>
  );

  return (
    <Modal isOpen onClose={close} title="Send to reps — check before sending" size="xl" className="rep-send" footer={footer}>
      <RepSendBody
        loading={loading} error={error} groups={groups} unassigned={unassigned} current={current} setCurrent={setCurrent}
        set={set} allTicked={allTicked} sendAs={sendAs} setSendAs={setSendAs} sending={sending}
        toggleAll={(on) => setGroups(prev => prev.map(x => (x.repId && x.status !== 'sent' ? { ...x, include: on } : x)))}
      />
      <ConfirmDialog
        isOpen={asking}
        title="Send to reps"
        type="info"
        message={`Send ${toSend.length} email${toSend.length === 1 ? '' : 's'}, each with its rep's spreadsheet: ${toSend.map(x => `${x.repName} (${x.to})`).join(', ')}?`}
        confirmText="Send"
        confirmIcon="send"
        onConfirm={() => { setAsking(false); send(); }}
        onCancel={() => setAsking(false)}
      />
    </Modal>
  );
}

/** Consecutive columns under one heading: [{ group, span }] (1.2.0). */
export function groupSpans(columns) {
  const out = [];
  for (const c of columns || []) {
    const last = out[out.length - 1];
    if (last && c.group && last.group === c.group) last.span += 1;
    else out.push({ group: c.group || null, span: 1 });
  }
  return out;
}

/** The window's body; mounts with the footer, so it measures the slide-out buttons. */
function RepSendBody({ loading, error, groups, unassigned, current, setCurrent, set, allTicked, toggleAll, sendAs, setSendAs, sending }) {
  useSlideOutButton([loading, groups.length, sending, current]);
  if (loading) return <LoadingSpinner />;
  const g = groups[current] || null;
  return (
    <>
      {error && <p className="pc-flag err"><span className="material-symbols-rounded">error</span>{error}</p>}
      {unassigned > 0 && (
        <p className="pc-flag warn"><span className="material-symbols-rounded">warning</span>
          {unassigned} line{unassigned === 1 ? '' : 's'} belong{unassigned === 1 ? 's' : ''} to customers with no rep, so there is no one to send {unassigned === 1 ? 'it' : 'them'} to.</p>
      )}
      <div className="rs-grid">
        <div className="rs-list">
          <div className="rs-list-head">
            <TickCheckbox id="rs-all" checked={allTicked} ariaLabel="Tick every rep" onChange={(e) => toggleAll(e.target.checked)} />
            <span>{groups.filter(x => x.repId).length} rep{groups.filter(x => x.repId).length === 1 ? '' : 's'}</span>
          </div>
          {groups.map((x, i) => (
            <div key={i} className={`rs-rep ${i === current ? 'on' : ''} ${x.repId ? '' : 'none'}`} onClick={() => setCurrent(i)}
              role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') setCurrent(i); }}>
              <span onClick={(e) => e.stopPropagation()}>
                <TickCheckbox id={`rs-${i}`} checked={!!x.include && x.status !== 'sent'} disabled={!x.repId || !x.to || x.status === 'sent'}
                  title={!x.repId ? 'No rep' : !x.to ? 'No email address for this rep' : ''}
                  onChange={(e) => set(i, { include: e.target.checked })} />
              </span>
              <span className="rs-rep-text">
                <b>{x.repName || 'No rep'}</b>
                <span className="pc-muted">{x.to || 'no email address'} · {x.requestCount} request{x.requestCount === 1 ? '' : 's'}</span>
                {x.secondCheck && <span className="rs-badge">Second check</span>}
                {x.status === 'sent' && <span className="rs-badge ok">Sent</span>}
                {x.status === 'sending' && <span className="rs-badge">Sending…</span>}
                {x.status === 'failed' && <span className="rs-badge err" title={x.message || ''}>Not sent</span>}
              </span>
            </div>
          ))}
        </div>
        <div className="rs-detail">
          {!g ? <p className="pc-muted">Nothing to send.</p> : !g.repId ? (
            <p className="pc-muted">These lines' customers have no rep. Give the customer a rep on its contact to ask for these prices.</p>
          ) : (
            <>
              {g.status === 'failed' && <p className="pc-flag err"><span className="material-symbols-rounded">error</span>{g.message}</p>}
              {g.status === 'sent' && <p className="pc-flag info"><span className="material-symbols-rounded">check_circle</span>{g.message}</p>}
              <div className="rs-fields">
                <label>To<Input value={g.to} onChange={(e) => set(current, { to: e.target.value })} disabled={g.status === 'sent'} /></label>
                <label>Cc<Input value={g.cc} onChange={(e) => set(current, { cc: e.target.value })} disabled={g.status === 'sent'} placeholder="Optional" /></label>
                <label>Send from<SenderSelect value={sendAs} onChange={setSendAs} disabled={sending} /></label>
                <label className="rs-wide">Subject<Input value={g.subject} onChange={(e) => set(current, { subject: e.target.value })} disabled={g.status === 'sent'} /></label>
                <label className="rs-wide">Email
                  <Textarea value={g.body} rows={10} onChange={(e) => set(current, { body: e.target.value })} disabled={g.status === 'sent'} />
                </label>
              </div>
              {g.sheet && (
                <div className="rs-sheet">
                  <div className="rs-sheet-head">
                    <span className="material-symbols-rounded">table_view</span>
                    <b>{g.sheet.filename}</b>
                    <span className="pc-muted">attached · {g.sheet.rows.length} row{g.sheet.rows.length === 1 ? '' : 's'}</span>
                  </div>
                  <p className="rs-sheet-title">{g.sheet.title}</p>
                  <p className="pc-muted">{g.sheet.note}</p>
                  <div className="rs-sheet-scroll">
                    <table className="data-table rs-sheet-table">
                      <thead>
                        {g.sheet.columns.some(c => c.group) && (
                          <tr className="rs-sheet-groups">{groupSpans(g.sheet.columns).map((sp, i) => (
                            <th key={i} colSpan={sp.span} className={sp.group ? 'grouped' : ''}>{sp.group || ''}</th>
                          ))}</tr>
                        )}
                        <tr>{g.sheet.columns.map(c => <th key={c.key} className={c.fillIn ? 'fill' : ''}>{c.header}</th>)}</tr>
                      </thead>
                      <tbody>
                        {g.sheet.rows.map((r, ri) => (
                          <tr key={ri} className={r.newCustomer ? 'new-customer' : ''}>
                            {r.cells.map((cell, ci) => (
                              <td key={ci} className={g.sheet.columns[ci]?.fillIn ? 'fill' : ''}>{String(cell)}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default RepSendModal;
